<template>
    <Dialog class="data-dialog confirm-delete-dialog" :visible="visible" :style="{ width: '32rem' }" modal
        :header="header" :closable="!loading" :closeOnEscape="!loading" @update:visible="onVisibleChange">

        <div class="confirm-delete-body">

            <div v-if="subjectId || subjectName" class="confirm-delete-subject">
                <span v-if="subjectName" class="confirm-delete-subject-name">{{ subjectName }}</span>
                <span v-if="subjectId" class="pill pill-muted">ID {{ subjectId }}</span>
            </div>

            <p class="confirm-delete-text"><slot /></p>

            <div class="confirm-delete-countdown" :class="{ 'is-released': isReleased }">
                <div class="confirm-delete-track">
                    <span class="confirm-delete-fill" :style="{ width: `${progress}%` }"></span>
                </div>
                <span class="confirm-delete-hint" aria-live="polite">
                    <i :class="isReleased ? 'pi pi-check-circle' : 'pi pi-clock'"></i>
                    <template v-if="isReleased">Confirmação habilitada.</template>
                    <template v-else>Aguarde a contagem para habilitar a confirmação.</template>
                </span>
            </div>

        </div>

        <template #footer>
            <Button class="p-button-text" type="button" label="Cancelar" :disabled="loading" @click="close" />
            <Button class="p-button-danger" type="button" icon="pi pi-trash" :label="confirmButtonLabel"
                :loading="loading" :disabled="!isReleased || loading" @click="confirm" />
        </template>

    </Dialog>
</template>

<script>
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { computed, onBeforeUnmount, reactive, watch } from 'vue'

// intervalo curto para a barra de progresso andar de forma contínua, não em saltos de 1 s
const TICK_MS = 100

export default {
    name: 'ConfirmDeleteDialog',

    components: { Button, Dialog },

    props: {
        visible: {
            type: Boolean,
            default: false
        },
        header: {
            type: String,
            default: 'Confirmar exclusão'
        },
        // identificação do registro, exibida acima do aviso
        subjectId: {
            type: [String, Number],
            default: null
        },
        subjectName: {
            type: String,
            default: ''
        },
        confirmLabel: {
            type: String,
            default: 'Confirmar exclusão'
        },
        // requisição de exclusão em andamento: trava o diálogo até a resposta
        loading: {
            type: Boolean,
            default: false
        },
        // segundos de espera antes de liberar o botão de confirmação
        countdown: {
            type: Number,
            default: 5
        }
    },

    emits: ['update:visible', 'confirm'],

    setup(props, { emit }) {
        const state = reactive({
            elapsed: 0
        })

        let timer = null

        const totalMs = computed(() => Math.max(props.countdown, 0) * 1000)
        const isReleased = computed(() => state.elapsed >= totalMs.value)
        const remainingSeconds = computed(() => Math.ceil((totalMs.value - state.elapsed) / 1000))
        const progress = computed(() => {
            if (!totalMs.value) return 100
            return Math.min((state.elapsed / totalMs.value) * 100, 100)
        })

        const confirmButtonLabel = computed(() => {
            return isReleased.value ? props.confirmLabel : `${props.confirmLabel} (${remainingSeconds.value})`
        })

        // a contagem recomeça a cada abertura: reabrir o diálogo nunca entrega o botão já liberado
        watch(() => props.visible, (isVisible) => {
            isVisible ? startCountdown() : stopCountdown()
        }, { immediate: true })

        onBeforeUnmount(stopCountdown)

        function startCountdown() {
            stopCountdown()
            state.elapsed = 0

            if (!totalMs.value) return

            timer = setInterval(() => {
                state.elapsed += TICK_MS

                if (state.elapsed >= totalMs.value) {
                    state.elapsed = totalMs.value
                    stopCountdown()
                }
            }, TICK_MS)
        }

        function stopCountdown() {
            if (!timer) return
            clearInterval(timer)
            timer = null
        }

        function onVisibleChange(value) {
            emit('update:visible', value)
        }

        function close() {
            emit('update:visible', false)
        }

        function confirm() {
            if (!isReleased.value || props.loading) return
            emit('confirm')
        }

        return {
            state,
            isReleased,
            remainingSeconds,
            progress,
            confirmButtonLabel,
            onVisibleChange,
            close,
            confirm
        }
    }
}
</script>
